import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode, SVGProps } from 'react';
import { api, type Salon } from '../lib/api';
import { useT, type TKey } from '../lib/i18n';
import { useLocations } from '../lib/location';
import { CardIcon, ClockIcon, LinkIcon, LockIcon, MailIcon, PinIcon, ScissorsIcon, StoreIcon, TeamIcon, UserIcon } from './icons';

type To =
  | '/s/$orgId/salon/diensten'
  | '/s/$orgId/salon/team'
  | '/s/$orgId/salon/openingstijden'
  | '/s/$orgId/salon/locaties'
  | '/s/$orgId/salon/online-boeken'
  | '/s/$orgId/salon/berichten'
  | '/s/$orgId/salon/bedrijf'
  | '/s/$orgId/salon/privacy'
  | '/s/$orgId/salon/abonnement'
  | '/s/$orgId/account';
type NavItem = { key: TKey; to: To; Icon: (p: SVGProps<SVGSVGElement>) => ReactNode };

/** The Salon settings in the same three groups as the hub, plus your own account. */
function useNav(orgId: string): Array<{ title: TKey; items: NavItem[] }> {
  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const locations = useLocations(orgId);
  const you = { title: 'hub.you' as TKey, items: [{ key: 'acc.title' as TKey, to: '/s/$orgId/account' as const, Icon: UserIcon }] };
  if (salon.data?.role === 'staff') return [you];
  return [
    {
      title: 'salon.group.salon',
      items: [
        { key: 'salon.services', to: '/s/$orgId/salon/diensten', Icon: ScissorsIcon },
        { key: 'salon.team', to: '/s/$orgId/salon/team', Icon: TeamIcon },
        { key: 'salon.hours', to: '/s/$orgId/salon/openingstijden', Icon: ClockIcon },
        ...((locations.data?.length ?? 0) > 1 ? [{ key: 'loc.title' as TKey, to: '/s/$orgId/salon/locaties' as const, Icon: PinIcon }] : []),
      ],
    },
    {
      title: 'salon.group.reach',
      items: [
        { key: 'salon.booking', to: '/s/$orgId/salon/online-boeken', Icon: LinkIcon },
        { key: 'salon.messages', to: '/s/$orgId/salon/berichten', Icon: MailIcon },
      ],
    },
    {
      title: 'salon.group.account',
      items: [
        { key: 'salon.business', to: '/s/$orgId/salon/bedrijf', Icon: StoreIcon },
        { key: 'salon.privacy', to: '/s/$orgId/salon/privacy', Icon: LockIcon },
        ...(salon.data?.role === 'owner' ? [{ key: 'salon.subscription' as TKey, to: '/s/$orgId/salon/abonnement' as const, Icon: CardIcon }] : []),
      ],
    },
    you,
  ];
}

function SettingsNav({ orgId }: { orgId: string }) {
  const t = useT();
  const groups = useNav(orgId);
  return (
    <nav aria-label={t('nav.salon')} className="sticky top-6 flex flex-col gap-5">
      <Link to="/s/$orgId/salon" params={{ orgId }} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-ink">
        <span aria-hidden>←</span> {t('nav.salon')}
      </Link>
      {groups.map((g) => (
        <div key={g.title} className="flex flex-col gap-1">
          <p className="eyebrow px-3 pb-1">{t(g.title)}</p>
          {g.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              params={{ orgId }}
              className="group relative flex items-center gap-3 rounded-full px-3 py-2 text-[15px] text-muted transition-colors hover:bg-subtle hover:text-ink"
              activeProps={{ className: '!bg-surface !text-ink font-medium shadow-[0_1px_2px_rgb(2_53_42/0.06)]', 'aria-current': 'page' }}
            >
              {/* The ribbon marks where you are, as in the rail. */}
              <span aria-hidden className="absolute -left-1 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full bg-accent opacity-0 group-aria-[current=page]:opacity-100" />
              <item.Icon className="size-[18px] shrink-0" />
              {t(item.key)}
            </Link>
          ))}
        </div>
      ))}
    </nav>
  );
}

/**
 * A settings page. Phones and laptops: one centred column. Wide screens: the section menu on
 * the left, the form in the middle and, beside it, how clients see what you set here.
 */
export function SettingsPage({ orgId, aside, children }: { orgId: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <div className="mx-auto grid w-full max-w-3xl gap-8 xl:max-w-none xl:grid-cols-[210px_minmax(0,1fr)] xl:gap-10 2xl:grid-cols-[210px_minmax(0,1fr)_420px]">
      <div className="hidden xl:block">
        <SettingsNav orgId={orgId} />
      </div>
      <div className="mx-auto flex w-full min-w-0 max-w-3xl flex-col gap-6 2xl:max-w-[920px] xl:[&_.settings-back]:hidden">{children}</div>
      <aside className="hidden 2xl:block">
        <div className="sticky top-6 flex flex-col gap-4">{aside}</div>
      </aside>
    </div>
  );
}
